// doctor-frontend/src/pages/CMFMLoginRegister.js
import React, { useState } from 'react';
import aiimsLogo from '../assets/aiims_logo.png';
import http from '../services/http';

/* ---------------- helpers ---------------- */

const ROLE_ALIASES = new Map([
  ['jr', 'junior resident'],
  ['junior', 'junior resident'],
  ['juniorresident', 'junior resident'],
  ['junior resident', 'junior resident'],
  ['sr', 'senior resident'],
  ['senior', 'senior resident'],
  ['seniorresident', 'senior resident'],
  ['senior resident', 'senior resident'],
  ['faculty', 'faculty'],
  ['prof', 'faculty'],
  ['professor', 'faculty'],
  ['admin', 'admin'],
  ['administrator', 'admin'],
  ['deo', 'deo'],
  ['data entry operator', 'deo'],
  ['data-entry-operator', 'deo'],
  ['student', 'medical student'],
  ['ug', 'medical student'],
  ['undergraduate', 'medical student'],
  ['medical student', 'medical student'],
  ['mbbs', 'medical student'],
  ['citizen', 'citizen'],
  ['medical officer', 'medical officer'],
  ['mo', 'medical officer'],
  ['medical-officer', 'medical officer'],
  ['lab technician', 'lab technician'],
  ['lab tech', 'lab technician'],
  ['laboratory', 'lab technician'],
  ['laboratory technician', 'lab technician'],
  ['radiographer', 'radiographer'],
  ['radiology', 'radiographer'],
  ['radiologist', 'radiographer'],
  ['nursing officer', 'nursing officer'],
  ['nurse', 'nursing officer'],
  ['nursing', 'nursing officer'],
  ['pharmacist', 'pharmacist'],
  ['pharmacy', 'pharmacist'],
  ['pharmacology', 'pharmacist'],
  ['hod', 'hod'],
  ['head of department', 'hod'],
]);

const normalizeRole = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (ROLE_ALIASES.has(normalized)) return ROLE_ALIASES.get(normalized);
  if (normalized.includes('admin')) return 'admin';

  // Do not silently convert an unknown role to Intern. Preserving the backend
  // value lets route/permission guards fail safely instead of granting the
  // wrong frontend workspace.
  return normalized;
};

function getTenantPrefix() {
  try {
    const match = (window.location.pathname || '').match(/^\/t\/[^/]+/);
    return match ? match[0] : '';
  } catch {
    return '';
  }
}

function getTenantSlug() {
  const prefix = getTenantPrefix();
  if (prefix) return prefix.replace(/^\/t\//, '');

  try {
    return String(localStorage.getItem('tenantSlug') || '').trim();
  } catch {
    return '';
  }
}

function tenantPath(path) {
  const base = getTenantPrefix();
  const clean = String(path || '').startsWith('/')
    ? String(path || '')
    : `/${String(path || '')}`;

  if (!base) return clean;
  if (clean.startsWith('/t/')) return clean;
  return `${base}${clean}`.replace(/\/{2,}/g, '/');
}

function getCitizenAppOrigin() {
  try {
    const envOrigin =
      typeof process !== 'undefined'
        ? String(process?.env?.REACT_APP_CITIZEN_ORIGIN || '').trim()
        : '';
    if (envOrigin) return envOrigin.replace(/\/+$/, '');
  } catch {}

  try {
    const legacyEnvOrigin =
      typeof process !== 'undefined'
        ? String(process?.env?.REACT_APP_CITIZEN_APP_ORIGIN || '').trim()
        : '';
    if (legacyEnvOrigin) return legacyEnvOrigin.replace(/\/+$/, '');
  } catch {}

  try {
    const runtimeOrigin = String(window?.__CITIZEN_APP_ORIGIN__ || '').trim();
    if (runtimeOrigin) return runtimeOrigin.replace(/\/+$/, '');
  } catch {}

  try {
    if (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    ) {
      return 'http://localhost:3001';
    }
  } catch {}

  return 'https://cmfm-citizen.mededtek.in';
}

function clearStorageKeys(storage, keys) {
  try {
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    // Storage may be unavailable in strict/private browser modes.
  }
}

/* Clear all known CMFM authentication keys, including legacy aliases. */
const clearCmfMSession = () => {
  clearStorageKeys(localStorage, [
    'cmfm_token',
    'cmfmToken',
    'authToken',
    'token',
    'loggedInUser',
    'cmfm_user',
    'role',
    'tenantId',
    'activeTenantId',
  ]);
};

/*
 * A newly authenticated staff member must not inherit another account's
 * selected Citizen or Visit from a shared browser session.
 */
const clearClinicalWorkspaceContext = () => {
  try {
    const activeCitizen = JSON.parse(
      localStorage.getItem('activeCitizen') || 'null'
    );
    const citizenId = String(
      activeCitizen?.citizenId ||
        localStorage.getItem('activeCitizenId') ||
        ''
    ).trim();
    const visitId = String(
      localStorage.getItem('activeVisitId') ||
        localStorage.getItem('visitId') ||
        ''
    ).trim();

    if (citizenId) localStorage.removeItem(`activeVisitId:${citizenId}`);
    if (visitId) localStorage.removeItem(`activeVisitContext:${visitId}`);
  } catch {}

  clearStorageKeys(localStorage, [
    'activeCitizen',
    'activeCitizenId',
    'selectedCitizen',
    'lastVisitedCitizenId',
    'activeVisitId',
    'visitId',
    'selectedVisit',
    'activeVisitContext',
    'editReturnedVisit',
    'activeCentre',
    'centre',
  ]);
};

function responsePayload(response) {
  return response && Object.prototype.hasOwnProperty.call(response, 'data')
    ? response.data
    : response;
}

function extractLoginResult(response) {
  const payload = responsePayload(response) || {};
  const nested = payload?.data || {};

  const directPayloadLooksLikeUser = Boolean(
    payload &&
      typeof payload === 'object' &&
      !Array.isArray(payload) &&
      (payload.role ||
        payload.userType ||
        payload.email ||
        payload.username ||
        payload._id ||
        payload.id)
  );

  const nestedPayloadLooksLikeUser = Boolean(
    nested &&
      typeof nested === 'object' &&
      !Array.isArray(nested) &&
      (nested.role ||
        nested.userType ||
        nested.email ||
        nested.username ||
        nested._id ||
        nested.id)
  );

  const user =
    payload?.user ||
    nested?.user ||
    payload?.result?.user ||
    (nestedPayloadLooksLikeUser ? nested : null) ||
    (directPayloadLooksLikeUser ? payload : null);

  const token =
    payload?.token ||
    nested?.token ||
    payload?.accessToken ||
    nested?.accessToken ||
    user?.token ||
    user?.accessToken ||
    '';

  const mustChangePassword = Boolean(
    payload?.mustChangePassword ??
      nested?.mustChangePassword ??
      user?.mustChangePassword
  );

  return {
    payload,
    user,
    token: String(token || '').trim(),
    mustChangePassword,
  };
}

function sanitizeStoredUser(user, role) {
  const source = user && typeof user === 'object' ? user : {};
  const {
    password: _password,
    token: _token,
    accessToken: _accessToken,
    refreshToken: _refreshToken,
    qrCode: _qrCode,
    ...safeUser
  } = source;

  return {
    ...safeUser,
    role,
  };
}

function extractTenantId(payload, user) {
  const value =
    user?.tenantId ||
    payload?.tenantId ||
    payload?.data?.tenantId ||
    user?.tenant?._id ||
    user?.tenant?.id ||
    payload?.tenant?._id ||
    payload?.tenant?.id ||
    payload?.data?.tenant?._id ||
    payload?.data?.tenant?.id;

  const tenantId = String(value || '').trim();
  return /^[a-f\d]{24}$/i.test(tenantId) ? tenantId : '';
}

/**
 * Save a validated session for the tenant doctor portal.
 *
 * Legacy token keys are retained during migration because existing pages still
 * read them. `cmfm_token` remains the canonical key.
 */
const storeSessionOnly = (response, fallbackRole = '') => {
  const { payload, user, token } = extractLoginResult(response);

  if (!user || typeof user !== 'object') {
    throw new Error('Login response did not include a valid user account.');
  }

  if (!token) {
    throw new Error('Login response did not include an authentication token.');
  }

  const role = normalizeRole(user?.role || user?.userType || fallbackRole);

  if (!role) {
    throw new Error(
      'This account has no configured CMFM role. Contact CMFM Admin.'
    );
  }

  const storedUser = sanitizeStoredUser(user, role);
  const tenantId = extractTenantId(payload, user);
  const tenantSlug = getTenantSlug();

  clearClinicalWorkspaceContext();
  clearCmfMSession();

  try {
    localStorage.setItem('cmfm_token', token);

    // Compatibility aliases for existing service/modules. Remove only after
    // all readers have migrated to `cmfm_token`.
    localStorage.setItem('cmfmToken', token);
    localStorage.setItem('authToken', token);
    localStorage.setItem('token', token);

    localStorage.setItem('loggedInUser', JSON.stringify(storedUser));
    localStorage.setItem('cmfm_user', JSON.stringify(storedUser));
    localStorage.setItem('role', role.toUpperCase());

    if (tenantId) {
      localStorage.setItem('tenantId', tenantId);
      localStorage.setItem('activeTenantId', tenantId);
    }

    if (tenantSlug) {
      localStorage.setItem('tenantSlug', tenantSlug);
    }
  } catch {
    throw new Error('Unable to store the login session in this browser.');
  }

  return {
    user: storedUser,
    token,
    role,
    tenantId,
  };
};

const storeSessionAndGo = (response, fallbackRole = '') => {
  storeSessionOnly(response, fallbackRole);
  window.location.replace(tenantPath('/dashboard'));
};

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    fallback
  );
}

function clearSameOriginCitizenAuthKeys() {
  // The Citizen portal is normally a different origin, so this cleanup only
  // affects same-origin compatibility keys. The Citizen frontend remains
  // responsible for its own session and logout state.
  const keys = [
    'citizenAuth',
    'citizenToken',
    'citizenUser',
    'citizenRefreshToken',
    'qrToken',
  ];

  clearStorageKeys(localStorage, keys);
  clearStorageKeys(sessionStorage, keys);
}

/* ---------------- banners (unchanged) ---------------- */

const BannerTop = () => (
  <div className="w-full flex justify-center items-start mb-2 mt-1">
    <svg
      width="100%"
      height="66"
      viewBox="0 0 700 66"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ maxWidth: 700 }}
      aria-hidden="true"
    >
      <rect x="40" y="28" width="90" height="16" rx="4" fill="#cbb6ea" />
      <rect x="60" y="14" width="18" height="16" rx="3" fill="#a68ac2" />
      <rect x="80" y="18" width="18" height="12" rx="2" fill="#b5a3ce" />
      <rect x="105" y="8" width="13" height="21" rx="2" fill="#83679a" />
      <circle cx="670" cy="20" r="14" fill="#e7e2f5" />
      <rect x="200" y="25" width="4" height="30" rx="2" fill="#a68ac2" />
      <ellipse
        cx="202"
        cy="25"
        rx="7"
        ry="3"
        fill="#a68ac2"
        opacity="0.3"
      />
      <path
        d="M0 60 Q200 30 400 60 Q550 80 700 55"
        stroke="#bba4db"
        strokeWidth="3"
        fill="none"
      />
    </svg>
  </div>
);

const BannerBottom = () => (
  <div className="w-full flex justify-center items-end mt-2">
    <svg
      width="100%"
      height="80"
      viewBox="0 0 700 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ maxWidth: 700 }}
      aria-hidden="true"
    >
      <ellipse cx="350" cy="75" rx="350" ry="30" fill="#ede9f6" />
      <path
        d="M0 70 Q100 50 180 75 Q300 95 400 55 Q550 10 700 75"
        stroke="#a68ac2"
        strokeWidth="3"
        fill="none"
      />
      <path
        d="M110 65 Q115 60 120 65 Q125 70 130 60 Q135 55 140 65"
        stroke="#a68ac2"
        strokeWidth="2"
        fill="none"
      />
      <rect x="520" y="35" width="30" height="30" fill="#b5a3ce" />
      <rect x="555" y="45" width="25" height="20" fill="#b5a3ce" />
      <circle cx="650" cy="35" r="14" fill="#e7e2f5" />
      <path
        d="M180 75 Q200 60 210 57 L220 77 L230 55 L240 78 Q260 82 270 72"
        stroke="#a68ac2"
        strokeWidth="2"
        fill="none"
      />
      <path
        d="M300 70 Q320 45 340 75 Q350 80 360 75"
        stroke="#a68ac2"
        strokeWidth="2"
        fill="none"
      />
    </svg>
  </div>
);

/* ---------------- component ---------------- */
const CMFMLoginRegister = () => {
  const [form, setForm] = useState({
    email: '',
    password: '',
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [mustChangePw, setMustChangePw] = useState(false);

  const [pwForm, setPwForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });

  const [pwBusy, setPwBusy] = useState(false);
  const [pwMsg, setPwMsg] = useState('');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setError('');
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  function buildLoginPayload(rawIdentifier, password) {
    const identifier = String(rawIdentifier || '').trim();
    const payload = { password };

    const isEmail = identifier.includes('@');
    const compactMobile = identifier.replace(/[\s()-]/g, '');
    const isMobile = /^\+?\d{10,15}$/.test(compactMobile);

    if (isEmail) {
      payload.email = identifier.toLowerCase();
    }

    if (isMobile) {
      payload.mobile = compactMobile;
    }

    // Preserve deployed login aliases used by older backend versions.
    payload.identifier = identifier;
    payload.username = identifier;
    payload.srId = identifier;

    return payload;
  }

  const doLogin = async () => {
    const identifier = String(form.email || '').trim();
    const password = String(form.password || '');

    if (!identifier || !password) {
      setError('Please enter Email/Mobile/SR ID and Password.');
      return;
    }

    setError('');
    setPwMsg('');
    setBusy(true);

    try {
      const payload = buildLoginPayload(identifier, password);
      const response = await http.post('/api/users/login', payload);
      const result = extractLoginResult(response);

      if (result.payload?.success === false) {
        throw new Error(
          result.payload?.message || 'Invalid credentials'
        );
      }

      if (!result.user || !result.token) {
        throw new Error(
          'Invalid login response. Please contact CMFM Admin.'
        );
      }

      const role = normalizeRole(
        result.user?.role || result.user?.userType
      );

      if (!role) {
        throw new Error(
          'This account has no configured CMFM role. Contact CMFM Admin.'
        );
      }

      // Citizens must authenticate in the separate Citizen portal.
      if (role === 'citizen') {
        clearCmfMSession();
        setError('Please use Citizen Login for citizen accounts.');
        return;
      }

      if (result.mustChangePassword) {
        storeSessionOnly(response, role);

        setPwForm({
          currentPassword: password,
          newPassword: '',
          confirmNewPassword: '',
        });

        setMustChangePw(true);
        return;
      }

      storeSessionAndGo(response, role);
    } catch (loginError) {
      setError(
        getErrorMessage(
          loginError,
          'Server error. Please try again.'
        )
      );

      clearCmfMSession();
    } finally {
      setBusy(false);
    }
  };

  const handleFirstLoginPasswordChange = async (event) => {
    event.preventDefault();

    if (pwBusy) {
      return;
    }

    setError('');
    setPwMsg('');

    const currentPassword = String(
      pwForm.currentPassword || ''
    );

    const newPassword = String(
      pwForm.newPassword || ''
    );

    const confirmNewPassword = String(
      pwForm.confirmNewPassword || ''
    );

    if (
      !currentPassword ||
      !newPassword ||
      !confirmNewPassword
    ) {
      setPwMsg('All password fields are required.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setPwMsg('New passwords do not match.');
      return;
    }

    if (newPassword.length < 6) {
      setPwMsg('New password must be at least 6 characters.');
      return;
    }

    if (newPassword === currentPassword) {
      setPwMsg(
        'New password must be different from the temporary password.'
      );
      return;
    }

    try {
      setPwBusy(true);

      const response = await http.post(
        '/api/users/change-password-first-login',
        {
          currentPassword,
          newPassword,
        }
      );

      const payload = responsePayload(response) || {};

      if (payload?.success === false) {
        throw new Error(
          payload?.message || 'Failed to update password.'
        );
      }

      // Required workflow: clear the temporary session and ask the user to
      // authenticate again with the new password.
      clearCmfMSession();
      setMustChangePw(false);

      setPwForm({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });

      setForm({
        email: form.email,
        password: '',
      });

      setPwMsg(
        'Password updated successfully. Please login again with your new password.'
      );
    } catch (passwordError) {
      setPwMsg(
        getErrorMessage(
          passwordError,
          'Failed to update password.'
        )
      );

      // If the temporary token is no longer usable, clear the partial session
      // and return to the normal login form rather than leaving a stuck state.
      if (
        [401, 403].includes(
          Number(passwordError?.response?.status || 0)
        )
      ) {
        clearCmfMSession();
        setMustChangePw(false);
      }
    } finally {
      setPwBusy(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (busy || mustChangePw) {
      return;
    }

    await doLogin();
  };

  const cancelFirstLoginPasswordChange = () => {
    clearCmfMSession();
    setMustChangePw(false);
    setPwMsg('');

    setPwForm({
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    });

    setForm((current) => ({
      ...current,
      password: '',
    }));
  };

  const switchToCitizen = () => {
    clearSameOriginCitizenAuthKeys();

    const origin = getCitizenAppOrigin();
    const target = `${origin}${tenantPath('/citizen-login')}`;

    window.location.assign(target);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#2d0036] to-[#1a0124] p-6">
      <div className="flex flex-row items-center justify-center w-full mt-6 mb-2 gap-4">
        <div
          className="bg-white rounded-full shadow-lg flex items-center justify-center"
          style={{
            width: 90,
            height: 90,
          }}
        >
          <img
            src={aiimsLogo}
            alt="AIIMS Jodhpur Logo"
            className="w-16 h-16 object-contain"
          />
        </div>

        <div className="flex flex-col justify-center items-center">
          <h1 className="text-xl font-bold text-orange-100 leading-tight text-center">
            Department of Community Medicine &amp; Family Medicine
          </h1>

          <h2 className="text-md text-orange-200 mt-1 text-center">
            AIIMS Jodhpur
          </h2>

          <p className="mt-1 font-semibold text-orange-200 text-center">
            Welcome
          </p>
        </div>
      </div>

      <div className="w-full max-w-3xl bg-white text-black rounded-3xl shadow-xl p-10 mt-1 mb-1 flex flex-col justify-start">
        <BannerTop />

        <h2 className="text-2xl font-semibold mb-3 text-center text-purple-800">
          CMFM Login
        </h2>

        {error && (
          <div
            role="alert"
            className="mb-3 px-3 py-2 rounded bg-red-50 text-red-700 border border-red-200 text-sm"
          >
            {error}
          </div>
        )}

        {pwMsg && !mustChangePw && (
          <div
            role="status"
            className="mb-3 px-3 py-2 rounded bg-green-50 text-green-800 border border-green-200 text-sm"
          >
            {pwMsg}
          </div>
        )}

        {mustChangePw && (
          <div className="mb-4 p-4 rounded-2xl border border-purple-200 bg-purple-50">
            <div className="font-semibold text-purple-900 mb-1">
              First-time login: Change Password
            </div>

            <div className="text-sm text-purple-800 mb-3">
              You are logged in with a temporary password. Set a new password
              before continuing.
            </div>

            {pwMsg && (
              <div
                role="alert"
                className="mb-3 px-3 py-2 rounded bg-white text-purple-900 border border-purple-200 text-sm"
              >
                {pwMsg}
              </div>
            )}

            <form
              onSubmit={handleFirstLoginPasswordChange}
              className="space-y-3"
              data-allow-submit="true"
            >
              <input
                name="currentPassword"
                type="password"
                value={pwForm.currentPassword}
                onChange={(event) =>
                  setPwForm((current) => ({
                    ...current,
                    currentPassword: event.target.value,
                  }))
                }
                placeholder="Current (temporary) password"
                className="w-full border p-2 rounded"
                required
                disabled={pwBusy}
                autoComplete="current-password"
              />

              <input
                name="newPassword"
                type="password"
                value={pwForm.newPassword}
                onChange={(event) =>
                  setPwForm((current) => ({
                    ...current,
                    newPassword: event.target.value,
                  }))
                }
                placeholder="New password"
                className="w-full border p-2 rounded"
                required
                disabled={pwBusy}
                autoComplete="new-password"
              />

              <input
                name="confirmNewPassword"
                type="password"
                value={pwForm.confirmNewPassword}
                onChange={(event) =>
                  setPwForm((current) => ({
                    ...current,
                    confirmNewPassword: event.target.value,
                  }))
                }
                placeholder="Confirm new password"
                className="w-full border p-2 rounded"
                required
                disabled={pwBusy}
                autoComplete="new-password"
              />

              <button
                type="submit"
                className={`w-full ${
                  pwBusy
                    ? 'bg-gray-300'
                    : 'bg-purple-800 hover:bg-purple-900'
                } text-white font-semibold px-4 py-2 rounded-2xl shadow transition duration-200`}
                disabled={pwBusy}
              >
                {pwBusy ? 'Updating…' : 'Update Password'}
              </button>

              <button
                type="button"
                onClick={cancelFirstLoginPasswordChange}
                className="w-full border border-purple-300 text-purple-900 font-semibold px-4 py-2 rounded-2xl hover:bg-purple-100 transition duration-200"
                disabled={pwBusy}
              >
                Cancel and return to login
              </button>
            </form>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-5"
          data-allow-submit="true"
        >
          <input
            name="email"
            type="text"
            value={form.email}
            onChange={handleChange}
            placeholder="Email / Mobile / SR ID / Username"
            className="w-full border p-2 rounded"
            required
            disabled={busy || mustChangePw}
            autoComplete="username"
            aria-label="Email, mobile, SR ID or username"
          />

          <input
            name="password"
            type="password"
            value={form.password}
            onChange={handleChange}
            placeholder="Password"
            className="w-full border p-2 rounded"
            required
            disabled={busy || mustChangePw}
            autoComplete="current-password"
            aria-label="Password"
          />

          <button
            type="submit"
            className={`w-full ${
              busy
                ? 'bg-gray-300'
                : 'bg-[#2d0036] hover:bg-[#210023]'
            } text-white font-semibold px-4 py-2 rounded-2xl shadow transition duration-200 mt-1`}
            disabled={busy || mustChangePw}
          >
            {busy ? 'Signing in…' : 'Login'}
          </button>
        </form>

        <div className="flex justify-center mt-4">
          <button
            type="button"
            className="bg-gray-200 text-gray-500 font-semibold px-4 py-2 rounded-2xl shadow border border-gray-300 transition duration-200 w-full cursor-not-allowed"
            disabled
            title="Disabled during Admin-only lock"
          >
            Login with QR Code (Disabled)
          </button>
        </div>

        <div className="flex justify-start mt-1">
          <button
            type="button"
            className="text-sm text-orange-800 hover:underline text-left"
            onClick={() =>
              setError(
                'Password reset is currently handled by CMFM Admin. Please contact CMFM Admin for reset.'
              )
            }
            style={{
              minWidth: 'fit-content',
              padding: 0,
            }}
            disabled={busy || mustChangePw}
          >
            Forgot Password / QR Code? / पासवर्ड या क्यूआर कोड भूल गए?
          </button>
        </div>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={switchToCitizen}
            className="text-sm text-purple-900 hover:underline"
            disabled={busy || mustChangePw}
          >
            Switch to Citizen Login
          </button>
        </div>

        <BannerBottom />
      </div>
    </div>
  );
};

export default CMFMLoginRegister;
